import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

const HelpContext = createContext(null);

const initialState = {
  helpPanelOpen: false,
  helpPanelSearch: '',
};

function helpReducer(state, action) {
  switch (action.type) {
    case 'OPEN_HELP_PANEL':
      return { ...state, helpPanelOpen: true };

    case 'CLOSE_HELP_PANEL':
      return { ...state, helpPanelOpen: false };

    case 'TOGGLE_HELP_PANEL':
      return { ...state, helpPanelOpen: !state.helpPanelOpen };

    case 'SET_SEARCH':
      return { ...state, helpPanelSearch: action.query };

    default:
      return state;
  }
}

export const HelpProvider = ({ children }) => {
  const [state, dispatch] = useReducer(helpReducer, initialState);

  // Global "?" keyboard shortcut to toggle help panel
  useEffect(() => {
    const handler = (e) => {
      if (e.key === '?') {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (document.activeElement?.contentEditable === 'true') return;
        e.preventDefault();
        dispatch({ type: 'TOGGLE_HELP_PANEL' });
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const openHelpPanel = useCallback(() => {
    dispatch({ type: 'OPEN_HELP_PANEL' });
  }, []);

  const closeHelpPanel = useCallback(() => {
    dispatch({ type: 'CLOSE_HELP_PANEL' });
  }, []);

  const setHelpPanelSearch = useCallback((query) => {
    dispatch({ type: 'SET_SEARCH', query });
  }, []);

  const value = {
    // State
    helpPanelOpen: state.helpPanelOpen,
    helpPanelSearch: state.helpPanelSearch,

    // Actions
    openHelpPanel,
    closeHelpPanel,
    setHelpPanelSearch,
  };

  return (
    <HelpContext.Provider value={value}>
      {children}
    </HelpContext.Provider>
  );
};

export const useHelp = () => {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error('useHelp must be used within HelpProvider');
  }
  return context;
};

export default HelpProvider;
